import Dropzone from "dropzone";
import { Controller } from "stimulus";
import { DirectUpload } from "@rails/activestorage";
import {
    getMetaValue,
    toArray,
    findElement,
    removeElement,
    insertAfter
} from "helpers";

function formSubmitHandler(event) {
    event.preventDefault();
    const choice = window.confirm('There are pending uploads, do you want to continue?');
    if (!choice) {
        event.stopPropagation();

        return;
    };

    this.submit();
}

export default class extends Controller {
    static targets = ["input", "filePreview"];

    connect() {
        this.dropZone = createDropZone(this);
        this.hideFileInput();
        this.bindEvents();
        Dropzone.autoDiscover = false; // necessary quirk for Dropzone error in console
    }

    get form() {
        return this.element.closest('form');
    }

    addSubmitHandler() {
        if (this.submitHandlerAdded) return;

        this.submitHandlerAdded = true;
        this.element.classList.toggle('dropzone-pulsing-background', true);
        this.form.addEventListener('submit', formSubmitHandler);
    }

    removeSubmitHandler() {
        this.form.removeEventListener('submit', formSubmitHandler);
        this.element.classList.toggle('dropzone-pulsing-background', false);
        this.submitHandlerAdded = false;
    }

    // Private
    hideFileInput() {
        this.inputTarget.disabled = true;
        this.inputTarget.style.display = "none";
    }

    bindEvents() {
        this.dropZone.on("addedfile", file => {
            setTimeout(() => {
                file.accepted && createDirectUploadController(this, file).start();
            }, 500);
        });

        this.dropZone.on("removedfile", file => {
            file.controller && removeElement(file.controller.hiddenInput);
        });

        this.dropZone.on("canceled", file => {
            file.controller && file.controller.xhr.abort();
        });

        this.dropZone.on("queuecomplete", file => {
            console.log('queuecomplete', file, 'allow submit');
            this.removeSubmitHandler();
        });
        this.dropZone.on("addedfiles", file => {
            this.addSubmitHandler();
            console.log('addedfiles', file);
        });
    }

    get headers() {
        return { "X-CSRF-Token": getMetaValue("csrf-token") };
    }

    get url() {
        return this.inputTarget.getAttribute("data-direct-upload-url");
    }

    get maxFiles() {
        return this.data.get("maxFiles") || 20;
    }

    get maxFileSize() {
        return this.data.get("maxFileSize") || 10;
    }

    get acceptedFiles() {
        return this.data.get("acceptedFiles") || ".jpeg,.jpg,.png";
    }

    get addRemoveLinks() {
        return this.data.get("addRemoveLinks") || false;
    }
}

class DirectUploadController {
    constructor(source, file) {
        this.directUpload = createDirectUpload(file, source.url, this);
        this.source = source;
        this.file = file;
    }

    start() {
        this.file.controller = this;
        this.hiddenInput = this.createHiddenInput();
        this.directUpload.create((error, attributes) => {
            if (error) {
                removeElement(this.hiddenInput);
                this.emitDropzoneError(error);
            } else {
                this.hiddenInput.value = attributes.signed_id;
                this.emitDropzoneSuccess();
            }
        });
    }

    createHiddenInput() {
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = this.source.inputTarget.name;
        insertAfter(input, this.source.inputTarget);
        return input;
    }

    directUploadWillStoreFileWithXHR(xhr) {
        this.bindProgressEvent(xhr);
        this.emitDropzoneUploading();
    }

    bindProgressEvent(xhr) {
        this.xhr = xhr;
        this.xhr.upload.addEventListener("progress", event =>
            this.uploadRequestDidProgress(event)
        );
    }

    uploadRequestDidProgress(event) {
        const element = this.source.element;
        const progress = (event.loaded / event.total) * 100;
        findElement(
            this.file.previewTemplate,
            ".dz-upload"
        ).style.width = `${progress}%`;
    }

    emitDropzoneUploading() {
        this.file.status = Dropzone.UPLOADING;
        this.source.dropZone.emit("processing", this.file);
    }

    emitDropzoneError(error) {
        this.file.status = Dropzone.ERROR;
        this.source.dropZone.emit("error", this.file, error);
        this.source.dropZone.emit("complete", this.file);
    }

    emitDropzoneSuccess() {
        this.file.status = Dropzone.SUCCESS;
        this.source.dropZone.emit("success", this.file);
        this.source.dropZone.emit("complete", this.file);
    }
}

function createDirectUploadController(source, file) {
    return new DirectUploadController(source, file);
}

function createDirectUpload(file, url, controller) {
    return new DirectUpload(file, url, controller);
}

function createDropZone(controller) {

    return new Dropzone(controller.element, {
        url: controller.url,
        headers: controller.headers,
        maxFiles: controller.maxFiles,
        maxFilesize: controller.maxFileSize,
        acceptedFiles: controller.acceptedFiles,
        addRemoveLinks: controller.addRemoveLinks,
        uploadMultiple: controller.maxFiles > 1,
        autoQueue: false,
        previewTemplate: controller.filePreviewTarget.innerHTML
    });
}
